import * as Sentry from "@sentry/browser"

import { currentEnvironment, metaContent } from "@/lib/utils"

// Errors only. Session Replay and performance tracing are deliberately out of
// scope (#1785) — the volume and privacy tradeoffs deserve their own decision —
// so neither browserTracingIntegration nor replayIntegration is registered.
// @sentry/browser ships neither by default, so omitting them is the whole of it.

// Junk that would otherwise dominate the issue stream. Browser extensions
// inject scripts into every page and their failures are reported against our
// DSN even though nobody here can fix them. Filtered from day one rather than
// retrofitted after a noisy week.
//
// Deliberately NOT filtered: network and fetch failures. The invisible half of
// an ActiveStorage direct upload is a PUT from the browser straight to Tigris,
// and that is exactly the failure this SDK exists to catch (#1784).
export const IGNORE_ERRORS: string[] = [
  // Benign layout-loop notifications. Fire on scroll in Chrome and Safari with
  // no user-visible effect.
  "ResizeObserver loop limit exceeded",
  "ResizeObserver loop completed with undelivered notifications",
  // The persistent audio player swaps sources constantly, and every browser
  // rejects the in-flight play() promise when it does. Benign by construction:
  // the newer load or pause is what the listener asked for.
  "The play() request was interrupted by a new load request",
  "The play() request was interrupted by a call to pause()",
  // Autoplay policy, not a fault: the browser refuses playback that the
  // listener has not gestured for yet. Every audio-first site sees these.
  "play() failed because the user didn't interact with the document first",
  "The request is not allowed by the user agent or the platform in the current context",
  // Extension globals — never ours.
  "top.GLOBALS",
  "_avast_submit",
  // Injected by the in-app browser in Bing/Edge on iOS.
  "instantSearchSDKJSBridgeClearHighlight",
  // Outlook SafeLink crawler. The exact string only, so genuine non-Error
  // rejections still come through.
  "Non-Error promise rejection captured with value: Object Not Found Matching Id",
]

// Matched against the top stack frame's URL. Scheme-anchored on purpose: an
// unanchored rule would silently swallow same-origin paths that happen to
// contain the same words.
export const DENY_URLS: RegExp[] = [
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,
  /^safari-(web-)?extension:\/\//i,
  /^ms-browser-extension:\/\//i,
  // Pages opened straight off disk are never our deployed bundle.
  /^file:\/\//i,
]

let initialized = false

// Safe to call from either entrypoint; the second call is a no-op.
export function initializeSentry(): void {
  if (initialized) return

  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined
  if (!dsn) return

  Sentry.init({
    dsn,
    environment: currentEnvironment(),
    ignoreErrors: IGNORE_ERRORS,
    denyUrls: DENY_URLS,
  })
  initialized = true

  setSentryUserFromDocument()
}

// Only the user id crosses into the browser. Sentry resolves the person from
// it, the server-side event carries the email already, and an email in the DOM
// of every page is readable by every third-party script on it.
export function setSentryUser(id?: string | null): void {
  Sentry.setUser(id ? { id } : null)
}

// The Turbo/listener experience has no props to read, so identity arrives as a
// meta tag (SentryHelper). Turbo merges the new page's head on navigation, so
// re-reading on turbo:load is what picks up a sign-in or sign-out.
export function setSentryUserFromDocument(): void {
  setSentryUser(metaContent("sentry-user-id"))
}

// The global handlers Sentry.init installs see uncaught exceptions and
// UNHANDLED rejections. An ActiveStorage direct-upload failure is neither:
// useDirectUpload rejects, and every caller catches — so the browser-to-Tigris
// PUT that motivated all of this (#1784) would still leave no trace. Reported
// once here, at the source, rather than asking every caller to remember.
export function captureDirectUploadFailure(
  error: Error,
  file: { name: string; size: number; type: string },
): void {
  Sentry.captureException(error, {
    tags: { failure: "direct_upload" },
    extra: {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
    },
  })
}

interface InertiaPage {
  props: { auth?: { user?: { id?: string | number } | null } }
}

interface InertiaRouter {
  on(
    event: "success",
    callback: (event: { detail: { page: unknown } }) => void,
  ): void
}

// The creator dashboard already ships identity as an Inertia prop, and its
// visits — including Log out, which is a `<Link method="delete">` — never
// reload the document. Reading the meta tag once at boot would leave the
// departed user attached for the rest of the tab, so identity is re-read from
// the props of every page Inertia renders.
export function trackSentryUserAcrossInertiaVisits(
  router: InertiaRouter,
): void {
  router.on("success", (event) => {
    setSentryUserFromInertiaPage(event.detail.page)
  })
}

// A partial reload whose component changes arrives without the shared props
// merged in, so an absent `auth` key means "no information", not "signed out".
// Clearing on it would anonymise a signed-in creator for the rest of the tab.
export function setSentryUserFromInertiaPage(page: unknown): void {
  const auth = (page as InertiaPage | undefined)?.props?.auth
  if (auth === undefined) return

  const id = auth.user?.id
  setSentryUser(id == null ? null : String(id))
}
